/**
 * custom-store-locator.js
 * Moxie — AE Store Locator
 * Google Maps JS API + brand filter + open/close + detail dialog + geolocation
 */

class StoreLocator extends HTMLElement {
  connectedCallback() {
    this._stores = JSON.parse(this.querySelector('[data-stores-json]').textContent);
    this._apiKey = this.dataset.apiKey;
    this._zoom = parseInt(this.dataset.zoom, 10) || 11;
    this._map = null;
    this._markers = [];
    this._infoWindow = null;
    this._activeFilter = 'all';
    this._activeMarkerIndex = null;
    this._observer = null;

    if (!this._apiKey) {
      this.querySelector('.store-locator__map').innerHTML =
        '<p class="store-locator__no-api">Configura tu Google Maps API key en los ajustes de la sección.</p>';
      return;
    }

    this._injectOpenStatus();
    this._bindTabs();
    this._bindDetailButtons();
    this._observeMap();
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    this._markers = [];
    this._map = null;
    this._infoWindow = null;
  }

  // ─── Lazy load Maps via IntersectionObserver ──────────────────────────────

  _observeMap() {
    if (!('IntersectionObserver' in window)) {
      this._loadGoogleMaps();
      return;
    }
    this._observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        this._observer.disconnect();
        this._loadGoogleMaps();
      },
      { rootMargin: '200px' }
    );
    this._observer.observe(this);
  }

  // ─── Open/Closed Logic ────────────────────────────────────────────────────

  _getOpenStatus(store) {
    const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Guayaquil' }));
    const day = now.getDay(); // 0 = Sun, 1–4 = Mon–Thu, 5–6 = Fri–Sat

    const hoursStr = day === 0
      ? store.hours_sun
      : day >= 5
        ? store.hours_fri_sat
        : store.hours_mon_thu;

    if (!hoursStr?.includes('-')) return null;

    const [openStr, closeStr] = hoursStr.split('-');
    const [openH, openM] = openStr.trim().split(':').map(Number);
    const [closeH, closeM] = closeStr.trim().split(':').map(Number);

    if (isNaN(openH) || isNaN(closeH)) return null;

    const nowMin = now.getHours() * 60 + now.getMinutes();
    const isOpen = nowMin >= openH * 60 + openM && nowMin < closeH * 60 + closeM;

    const fmt = (h, m) => {
      const period = h >= 12 ? 'PM' : 'AM';
      return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${period}`;
    };

    return {
      isOpen,
      html: isOpen
        ? `<span class="store-locator__open">Abierto</span> · Cierra a las ${fmt(closeH, closeM)}`
        : `<span class="store-locator__closed">Cerrado</span> · Abre a las ${fmt(openH, openM)}`
    };
  }

  _injectOpenStatus() {
    for (const [i, store] of this._stores.entries()) {
      const status = this._getOpenStatus(store);
      if (!status) continue;

      const item = this.querySelector(`.store-locator__item[data-index="${i}"]`);
      const nameEl = item?.querySelector('.store-locator__item-name');
      if (!nameEl) continue;

      const el = document.createElement('p');
      el.className = 'store-locator__item-status';
      el.innerHTML = status.html;
      nameEl.insertAdjacentElement('afterend', el);
    }
  }

  // ─── Mobile Tab Switcher ──────────────────────────────────────────────────

  _bindTabs() {
    const inner = this.querySelector('.store-locator__inner');
    if (!inner) return;

    inner.dataset.activeTab = 'list';

    for (const btn of this.querySelectorAll('.store-locator__tab')) {
      btn.addEventListener('click', () => {
        const tab = btn.dataset.tab;
        inner.dataset.activeTab = tab;

        for (const b of this.querySelectorAll('.store-locator__tab')) {
          b.classList.toggle('is-active', b.dataset.tab === tab);
          b.setAttribute('aria-selected', String(b.dataset.tab === tab));
        }

        if (tab === 'map' && this._map) {
          google.maps.event.trigger(this._map, 'resize');
        }
      });
    }
  }

  // ─── Detail Dialog ────────────────────────────────────────────────────────

  _bindDetailButtons() {
    const dialog = this.querySelector('.store-locator__dialog');
    if (!dialog) return;

    // Event delegation — catches clicks from list AND from map info window
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('.store-locator__detail-btn');
      if (!btn) return;
      e.stopPropagation();
      this._openDetail(parseInt(btn.dataset.detailIndex, 10));
    });

    dialog.querySelector('.store-locator__dialog-close')
      ?.addEventListener('click', () => dialog.close());

    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.close();
    });
  }

  _openDetail(index) {
    const store = this._stores[index];
    const dialog = this.querySelector('.store-locator__dialog');
    if (!dialog) return;

    // Close map info window if open
    this._infoWindow?.close();

    // Brand accent color
    const brandColors = { ae: '#182C52', aerie: '#D2EA8E', aeo_outlet: '#E51B23' };
    dialog.style.setProperty('--sl-dialog-accent', brandColors[store.brand] || 'rgb(var(--color-foreground))');

    // Name
    dialog.querySelector('.store-locator__dialog-name').textContent = store.name;

    // Schedule — build DOM nodes, no innerHTML with user data
    const schedEl = dialog.querySelector('.store-locator__dialog-schedule');
    schedEl.innerHTML = '';
    if (store.schedule) {
      for (const row of store.schedule.split('\n').filter(l => l.trim())) {
        const colonIdx = row.indexOf(':');
        const p = document.createElement('p');
        p.className = 'store-locator__dialog-schedule-row';

        if (colonIdx !== -1) {
          const day = document.createElement('span');
          day.className = 'store-locator__dialog-day';
          day.textContent = `${row.slice(0, colonIdx).trim()}:`;
          const hours = document.createElement('span');
          hours.textContent = row.slice(colonIdx + 1).trim();
          p.append(day, hours);
        } else {
          p.textContent = row;
        }
        schedEl.appendChild(p);
      }
    }

    // Details — build DOM nodes
    const detailsEl = dialog.querySelector('.store-locator__dialog-details');
    detailsEl.innerHTML = '';

    const addrParts = [store.address, store.city, store.zip].filter(Boolean);
    if (addrParts.length) {
      const p = document.createElement('p');
      p.className = 'store-locator__dialog-address';
      p.textContent = addrParts.join(', ');
      detailsEl.appendChild(p);
    }

    if (store.phone) {
      const p = document.createElement('p');
      p.className = 'store-locator__dialog-phone';
      p.textContent = store.phone;
      detailsEl.appendChild(p);
    }

    if (store.maps_url) {
      const a = document.createElement('a');
      a.href = store.maps_url;
      a.target = '_blank';
      a.rel = 'noopener';
      a.className = 'store-locator__link';
      a.textContent = 'Ver en Google Maps';
      detailsEl.appendChild(a);
    }

    if (dialog.open) dialog.close();
    dialog.showModal();
  }

  // ─── Google Maps Load ─────────────────────────────────────────────────────

  _loadGoogleMaps() {
    if (window.google?.maps) {
      this._init();
      return;
    }

    window.__storeLocatorReady = () => {
      delete window.__storeLocatorReady;
      this._init();
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${this._apiKey}&loading=async&callback=__storeLocatorReady`;
    script.async = true;
    document.head.appendChild(script);
  }

  _init() {
    const { lat, lng } = this._stores[0];
    const center = { lat: parseFloat(lat), lng: parseFloat(lng) };

    this._map = new google.maps.Map(this.querySelector('.store-locator__map'), {
      center,
      zoom: this._zoom,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      styles: [
        { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }
      ]
    });

    this._infoWindow = new google.maps.InfoWindow();
    this._buildMarkers();
    this._bindListItems();
    this._bindFilters();
    this._bindGeolocation();
  }

  // ─── Markers ──────────────────────────────────────────────────────────────

  _markerIcon(color, active = false) {
    return {
      path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z',
      fillColor: active ? '#fff' : color,
      fillOpacity: 1,
      strokeColor: active ? color : '#fff',
      strokeWeight: active ? 2.5 : 1.5,
      scale: active ? 2.2 : 1.8,
      anchor: new google.maps.Point(12, 22)
    };
  }

  _buildMarkers() {
    const brandColors = { ae: '#182C52', aerie: '#D2EA8E', aeo_outlet: '#E51B23' };
    const bounds = new google.maps.LatLngBounds();

    for (const [i, store] of this._stores.entries()) {
      const color = brandColors[store.brand] || '#182C52';
      const position = { lat: parseFloat(store.lat), lng: parseFloat(store.lng) };

      const marker = new google.maps.Marker({
        position,
        map: this._map,
        icon: this._markerIcon(color),
        title: store.name
      });

      marker._brand = store.brand;
      marker._color = color;
      marker._index = i;
      marker.addListener('click', () => this._selectStore(i));
      this._markers.push(marker);
      bounds.extend(position);
    }

    this._map.fitBounds(bounds);
    google.maps.event.addListenerOnce(this._map, 'bounds_changed', () => {
      if (this._map.getZoom() > 13) this._map.setZoom(13);
    });
  }

  // ─── List items ───────────────────────────────────────────────────────────

  _bindListItems() {
    for (const item of this.querySelectorAll('.store-locator__item')) {
      const index = parseInt(item.dataset.index, 10);

      item.addEventListener('click', (e) => {
        if (e.target.closest('a, .store-locator__detail-btn')) return;
        this._selectStore(index);
      });

      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._selectStore(index);
        }
      });
    }
  }

  // ─── Select store ─────────────────────────────────────────────────────────

  _selectStore(index) {
    const store = this._stores[index];
    const marker = this._markers[index];
    const status = this._getOpenStatus(store);

    // Reset previous active marker
    if (this._activeMarkerIndex !== null && this._activeMarkerIndex !== index) {
      const prev = this._markers[this._activeMarkerIndex];
      prev.setIcon(this._markerIcon(prev._color, false));
    }
    marker.setIcon(this._markerIcon(marker._color, true));
    this._activeMarkerIndex = index;

    // Highlight list item
    for (const el of this.querySelectorAll('.store-locator__item')) {
      el.classList.toggle('is-active', parseInt(el.dataset.index, 10) === index);
    }
    this.querySelector(`.store-locator__item[data-index="${index}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    // Info window
    const statusHtml = status ? `<p class="store-locator__popup-status">${status.html}</p>` : '';
    const phoneHtml = store.phone ? `<p class="store-locator__popup-phone">${store.phone}</p>` : '';

    this._infoWindow.setContent(`
      <div class="store-locator__popup">
        <p class="store-locator__popup-name">${store.name}</p>
        ${statusHtml}
        <p class="store-locator__popup-address">${store.address}${store.city ? `, ${store.city}` : ''}</p>
        ${phoneHtml}
        <div class="store-locator__popup-actions">
          ${store.maps_url ? `<a href="${store.maps_url}" target="_blank" rel="noopener">Ver Ubicación</a>` : ''}
          <button type="button" class="store-locator__detail-btn" data-detail-index="${index}">Detalles</button>
        </div>
      </div>
    `);

    this._infoWindow.open(this._map, marker);
    this._map.panTo(marker.getPosition());
    this._map.setZoom(Math.max(this._zoom, 14));
  }

  // ─── Filters ──────────────────────────────────────────────────────────────

  _bindFilters() {
    for (const btn of this.querySelectorAll('.store-locator__filter-btn')) {
      btn.addEventListener('click', () => {
        for (const b of this.querySelectorAll('.store-locator__filter-btn')) {
          b.classList.remove('is-active');
          b.removeAttribute('aria-pressed');
        }
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
        this._activeFilter = btn.dataset.filter;
        this._applyFilter();
      });
    }

    this.querySelector('.store-locator__filter-btn[data-filter="all"]')
      ?.setAttribute('aria-pressed', 'true');
  }

  _applyFilter() {
    const filter = this._activeFilter;
    let visible = 0;

    for (const item of this.querySelectorAll('.store-locator__item')) {
      const show = filter === 'all' || item.dataset.brand === filter;
      item.hidden = !show;
      if (show) visible++;
    }

    for (const marker of this._markers) {
      marker.setVisible(filter === 'all' || marker._brand === filter);
    }

    const countEl = this.querySelector('[data-store-count]');
    if (countEl) countEl.textContent = `${visible} Locales cercanos`;

    const emptyEl = this.querySelector('.store-locator__list-empty');
    if (emptyEl) emptyEl.hidden = visible > 0;

    this._infoWindow?.close();

    // Reset active marker if now hidden
    if (this._activeMarkerIndex !== null) {
      const active = this._stores[this._activeMarkerIndex];
      if (filter !== 'all' && active.brand !== filter) {
        const prev = this._markers[this._activeMarkerIndex];
        prev.setIcon(this._markerIcon(prev._color, false));
        this._activeMarkerIndex = null;
        for (const el of this.querySelectorAll('.store-locator__item')) {
          el.classList.remove('is-active');
        }
      }
    }
  }

  // ─── Geolocation ──────────────────────────────────────────────────────────

  _bindGeolocation() {
    const btn = this.querySelector('.store-locator__geolocate-btn');
    if (!btn || !navigator.geolocation) return;

    const iconSvg = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/><circle cx="12" cy="12" r="9" stroke-dasharray="2 4"/></svg>`;

    btn.addEventListener('click', () => {
      const label = btn.textContent.trim();
      btn.disabled = true;
      btn.innerHTML = `${iconSvg} Buscando...`;

      navigator.geolocation.getCurrentPosition(
        ({ coords: { latitude: uLat, longitude: uLng } }) => {
          btn.disabled = false;
          btn.innerHTML = `${iconSvg} ${label}`;

          const withDist = this._stores.map((store, i) => ({
            i,
            dist: this._haversine(uLat, uLng, parseFloat(store.lat), parseFloat(store.lng)),
            brand: store.brand
          }));

          // Show distance on each item
          for (const { i, dist } of withDist) {
            const el = this.querySelector(`.store-locator__item[data-index="${i}"] .store-locator__item-distance`);
            if (!el) continue;
            el.textContent = dist < 1 ? `${(dist * 1000).toFixed(0)} m` : `${dist.toFixed(1)} km`;
            el.hidden = false;
          }

          const nearest = withDist
            .filter(({ brand }) => this._activeFilter === 'all' || brand === this._activeFilter)
            .sort((a, b) => a.dist - b.dist)[0];

          if (nearest) this._selectStore(nearest.i);
        },
        () => {
          btn.disabled = false;
          btn.innerHTML = `${iconSvg} ${label}`;
        }
      );
    });
  }

  _haversine(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
}

customElements.define('store-locator', StoreLocator);
